import moment from "moment";
import i18n from "@/helpers/i18n.helper.js";

/**
 * Wspólna logika terminów posiedzenia.
 *
 * Posiedzenie ma zawsze termin główny (meeting_date + godzina) i może mieć ALBO
 * datę zakończenia (end_meeting_date - ciągły zakres dni), ALBO listę dodatkowych
 * terminów (additional_meeting_dates). Oba tryby wykluczają się wzajemnie.
 */

function toDate(value) {
    if (value === undefined || value === null || value === "") {
        return null;
    }

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : value;
    }

    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
}

function isSameDay(first, second) {
    return first.getFullYear() === second.getFullYear()
        && first.getMonth() === second.getMonth()
        && first.getDate() === second.getDate();
}

function startOfDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Wszystkie terminy posiedzenia (główny + dodatkowe), posortowane rosnąco.
 * @returns {Date[]}
 */
export function getSessionTerms(session) {
    const terms = [];
    const mainDate = toDate(session?.meeting_date);

    if (mainDate !== null) {
        terms.push(mainDate);
    }

    (session?.additional_meeting_dates ?? []).forEach((item) => {
        const additionalDate = toDate(item);
        if (additionalDate !== null) {
            terms.push(additionalDate);
        }
    });

    return terms.sort((first, second) => first - second);
}

/**
 * Czy posiedzenie korzysta z listy dodatkowych terminów.
 */
export function hasAdditionalTerms(session) {
    return (session?.additional_meeting_dates ?? []).length > 0;
}

/**
 * Ostatni dzień posiedzenia - ostatni z terminów, data zakończenia albo termin główny.
 * @returns {Date|null}
 */
export function getSessionLastTerm(session) {
    if (hasAdditionalTerms(session)) {
        const terms = getSessionTerms(session);
        return terms.length > 0 ? terms[terms.length - 1] : null;
    }

    return toDate(session?.end_meeting_date) ?? toDate(session?.meeting_date);
}

/**
 * Termin przypadający na wskazany dzień - używane do wyświetlenia właściwej godziny
 * w kalendarzu, gdy posiedzenie ma kilka terminów.
 * @returns {Date|null}
 */
export function getSessionTermForDate(session, date) {
    const day = toDate(date);

    if (day === null) {
        return null;
    }

    return getSessionTerms(session).find((term) => isSameDay(term, day)) ?? null;
}

/**
 * Czy posiedzenie odbywa się w danym dniu.
 */
export function isSessionOnDate(session, date) {
    const day = toDate(date);

    if (day === null) {
        return false;
    }

    if (getSessionTermForDate(session, day) !== null) {
        return true;
    }

    // tryb wielodniowy - ciągły zakres od meeting_date do end_meeting_date
    if (!hasAdditionalTerms(session)) {
        const mainDate = toDate(session?.meeting_date);
        const endDate = toDate(session?.end_meeting_date);

        if (mainDate !== null && endDate !== null) {
            const current = startOfDay(day).getTime();
            return current >= startOfDay(mainDate).getTime() && current <= startOfDay(endDate).getTime();
        }
    }

    return false;
}

/* ------------------------------------------------------------------ *
 * Obsługa listy dodatkowych terminów w modalach dodawania i edycji.   *
 * ------------------------------------------------------------------ */

/**
 * Pusty wiersz listy dodatkowych terminów.
 */
export function createEmptyAdditionalTerm() {
    return { date: "", time: "" };
}

/**
 * Klucz błędu walidacji dla wiersza listy - płaski string, taki jak reszta kluczy
 * obsługiwanych przez formValidation/main.js i dyrektywę v-error.
 */
export function additionalTermErrorKey(index, field) {
    return `additional_meeting_dates[${index}].${field}`;
}

/**
 * Zamienia listę ISO z API na wiersze formularza { date, time }.
 */
export function toAdditionalTermRows(additionalMeetingDates) {
    return (additionalMeetingDates ?? [])
        .map((item) => moment(item))
        .filter((item) => item.isValid())
        .sort((first, second) => first - second)
        .map((item) => ({ date: item.format("YYYY-MM-DD"), time: item.format("HH:mm") }));
}

/**
 * Zamienia wiersze formularza na payload wysyłany do API (pomija puste wiersze).
 */
export function toAdditionalTermPayload(rows) {
    return (rows ?? [])
        .filter((row) => row.date !== "" && row.time !== "")
        .map((row) => ({ date: row.date, time: row.time }));
}

/**
 * Waliduje listę dodatkowych terminów. Każdy kolejny termin musi być ostro
 * późniejszy niż poprzedni; termin główny posiedzenia jest terminem nr 0.
 *
 * @returns {Object} mapa { klucz_błędu: [komunikat] } - pusta, gdy wszystko jest poprawne
 */
export function validateAdditionalTerms(meetingDate, meetingTime, rows) {
    const errors = {};
    const t = i18n.global.t;

    if ((rows ?? []).length === 0) {
        return errors;
    }

    let previous = moment(`${meetingDate} ${meetingTime}`, "YYYY-MM-DD HH:mm", true);

    rows.forEach((row, index) => {
        const dateKey = additionalTermErrorKey(index, "date");
        const timeKey = additionalTermErrorKey(index, "time");

        if (!row.date) {
            errors[dateKey] = [t("com.global.required-field")];
        }
        else if (moment(row.date, "YYYY-MM-DD", true).isValid() === false) {
            errors[dateKey] = [t("com.validation-error.invalid-date-format")];
        }

        if (!row.time) {
            errors[timeKey] = [t("com.global.required-field")];
        }
        else if (moment(row.time, "HH:mm", true).isValid() === false) {
            errors[timeKey] = [t("com.validation-error.invalid-hour-format")];
        }

        if (errors[dateKey] || errors[timeKey]) {
            previous = null;
            return;
        }

        const current = moment(`${row.date} ${row.time}`, "YYYY-MM-DD HH:mm", true);

        if (previous !== null && previous.isValid() && current.isSameOrBefore(previous)) {
            const sameDay = current.isSame(previous, "day");
            errors[sameDay ? timeKey : dateKey] = [t("com.validation-error.additional-date-order-error")];
        }

        previous = current;
    });

    return errors;
}
